// Country-aware postal addresses (z8nrz7epeq).
//
// MIRRORED: this file is identical in cgt-backend (src/customers/address/) and
// cgt-frontend (src/lib/address/). The form, the API check and every printed
// address must agree, so they run the same code. Change both together.
//
// The rules per country come from address-formats.ts, generated from Google's
// address metadata (the data behind libaddressinput and browser autofill). A
// country outside that list gets ZZ, the generic fallback.

import { ADDRESS_FORMATS, type AddressFormat } from './address-formats.js'

/** The spec's address types; a partner's one address defaults to Shipping. */
export const ADDRESS_TYPES = ['Shipping', 'Billing', 'Business', 'Legal'] as const
export type AddressType = (typeof ADDRESS_TYPES)[number]

/** A postal address: the spec's address table, without its ids and flags. */
export interface PostalAddress {
  addressType?: AddressType | null
  companyName?: string | null
  attentionTo?: string | null
  addressLine1?: string | null
  addressLine2?: string | null
  addressLine3?: string | null
  city?: string | null
  districtLocality?: string | null
  /** The state/province/county as a name. */
  stateProvinceRegion?: string | null
  /** The same as it is written in an address: FL, ON, RM; null where a country has no list. */
  stateProvinceCode?: string | null
  postalCode?: string | null
  /** ISO 3166-1 alpha-2. */
  countryCode?: string | null
}

/** The fields a form lays out and a validation can fault. */
export type AddressField =
  | 'countryCode'
  | 'addressLine1'
  | 'addressLine2'
  | 'addressLine3'
  | 'companyName'
  | 'attentionTo'
  | 'districtLocality'
  | 'city'
  | 'state'
  | 'postalCode'

export type AddressErrors = Partial<Record<AddressField, string>>

/** Every ISO 3166-1 alpha-2 country code: what the country picker offers. */
export const ISO_COUNTRY_CODES = (
  'AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ ' +
  'CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO ' +
  'FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE ' +
  'JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO ' +
  'MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW ' +
  'PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM ' +
  'TN TO TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW'
).split(' ')
const ISO_SET = new Set(ISO_COUNTRY_CODES)

let displayNames: Intl.DisplayNames | null = null
/** "Belgium" for BE; the code itself when the runtime cannot name it. */
export function countryName(code: string | null | undefined): string {
  if (!code) return ''
  try {
    displayNames ??= new Intl.DisplayNames(['en'], { type: 'region' })
    return displayNames.of(code) ?? code
  } catch {
    return code
  }
}

/** Every country, by name, for a picker. */
export function countryOptions(): { code: string; name: string }[] {
  return ISO_COUNTRY_CODES.map((code) => ({ code, name: countryName(code) })).sort((a, b) =>
    a.name.localeCompare(b.name),
  )
}

/** What a form and a validation need to know about one country. */
export interface CountryRules {
  code: string
  /** False for a country on the generic fallback. */
  hasFullRules: boolean
  /** The city/state/postal-code fields, in the rows the country writes them. */
  localityRows: AddressField[][]
  /** Whether the country writes a state; where it does not, an optional Region is still recorded, unprinted. */
  usesState: boolean
  usesPostalCode: boolean
  required: Set<AddressField>
  labels: { city: string; state: string; postalCode: string; district: string }
  /** The country's state/province list, when it has one. */
  states: { code: string; name: string }[] | null
  postalPattern: RegExp | null
  /** Example postal codes, for the placeholder and messages. */
  postalExamples: string[]
}

const TOKEN_FIELD: Record<string, AddressField | undefined> = {
  N: 'attentionTo',
  O: 'companyName',
  D: 'districtLocality',
  C: 'city',
  S: 'state',
  Z: 'postalCode',
}

const LABELS: Record<string, string> = {
  // state_name_type
  state: 'State',
  province: 'Province',
  county: 'County',
  area: 'Area',
  department: 'Department',
  district: 'District',
  do_si: 'Province',
  emirate: 'Emirate',
  island: 'Island',
  oblast: 'Region',
  parish: 'Parish',
  prefecture: 'Prefecture',
  // zip_name_type
  zip: 'ZIP code',
  postal: 'Postal code',
  eircode: 'Eircode',
  pin: 'PIN code',
  // locality_name_type / sublocality_name_type
  city: 'City',
  post_town: 'Post town',
  suburb: 'Suburb',
  neighborhood: 'Neighborhood',
  townland: 'Townland',
  village_township: 'Village or township',
}
const label = (type: string | undefined, fallback: string) => (type && LABELS[type]) || fallback

const rulesCache = new Map<string, CountryRules>()

/**
 * The layout for a country without its own rules. Google's generic ZZ has no
 * state or postal code at all, which would hide both for some 200 countries
 * (Japan has postal codes); here both are offered, optional and free text.
 */
const FALLBACK_FMT = '%N%n%O%n%A%n%C%n%S %Z'

/** A country's format with the generic defaults filled in; the fallback layout when it has none of its own. */
function formatOf(code: string): { own: AddressFormat | undefined; f: AddressFormat } {
  const own: AddressFormat | undefined = code === 'ZZ' ? undefined : ADDRESS_FORMATS[code]
  return { own, f: { ...ADDRESS_FORMATS.ZZ, ...(own ?? { fmt: FALLBACK_FMT }) } }
}

/** The rules for a country code; the generic fallback for any other. */
export function rulesFor(countryCode: string | null | undefined): CountryRules {
  const code = (countryCode ?? '').trim().toUpperCase()
  const cached = rulesCache.get(code)
  if (cached) return cached

  const { own, f } = formatOf(code)
  const fmt = f.fmt ?? FALLBACK_FMT

  const localityRows: AddressField[][] = []
  for (const line of fmt.split('%n')) {
    const row = [...line.matchAll(/%([A-Z])/g)]
      .map((m) => TOKEN_FIELD[m[1]])
      .filter((field): field is AddressField => field === 'city' || field === 'state' || field === 'postalCode')
    if (row.length > 0) localityRows.push(row)
  }

  const usesState = fmt.includes('%S')
  const usesPostalCode = fmt.includes('%Z')
  const required = new Set<AddressField>(['countryCode', 'addressLine1', 'city'])
  for (const letter of f.require ?? '') {
    const field = letter === 'A' ? 'addressLine1' : TOKEN_FIELD[letter]
    if (field === 'state' && !usesState) continue
    if (field === 'postalCode' && !usesPostalCode) continue
    if (field === 'city' || field === 'state' || field === 'postalCode' || field === 'addressLine1') required.add(field)
  }

  const rules: CountryRules = {
    code,
    hasFullRules: !!own,
    localityRows,
    usesState,
    usesPostalCode,
    required,
    labels: {
      city: label(f.locality_name_type, 'City'),
      // A country that does not write one may still record its region (Limburg, BE).
      state: usesState ? label(f.state_name_type, 'Province') : 'Region',
      postalCode: label(f.zip_name_type, 'Postal code'),
      // Only a country that names it; Google's generic "suburb" reads oddly elsewhere.
      district: label(own?.sublocality_name_type, 'District'),
    },
    states: own?.subs ? own.subs.map(([key, name]) => ({ code: key, name: name ?? key })) : null,
    postalPattern: f.zip ? new RegExp(`^(?:${f.zip})$`, 'i') : null,
    postalExamples: (f.zipex ?? '').split(',').filter(Boolean),
  }
  rulesCache.set(code, rules)
  return rules
}

/** For matching what someone typed to a list entry: case- and accent-blind. */
const fold = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')

/** A state typed as its code or its name, as the country's list entry. */
export function findState(
  rules: CountryRules,
  typed: string | null | undefined,
): { code: string; name: string } | null {
  if (!rules.states || !typed?.trim()) return null
  const want = fold(typed)
  return rules.states.find((s) => fold(s.code) === want || fold(s.name) === want) ?? null
}

/**
 * A postal code in its country's written form: upper case, single spaces, and
 * the separator where the country puts one (`33602 1234` → `33602-1234`,
 * `sw1a1aa` → `SW1A 1AA`). Unchanged in shape when the country has no rule.
 */
export function normalizePostalCode(countryCode: string | null | undefined, raw: string | null | undefined): string {
  const value = (raw ?? '').trim().toUpperCase().replace(/\s+/g, ' ')
  if (!value) return ''
  const compact = value.replace(/[\s-]/g, '')
  const split = (at: number, sep = ' ') => `${compact.slice(0, at)}${sep}${compact.slice(at)}`
  switch ((countryCode ?? '').toUpperCase()) {
    case 'US':
      return /^\d{9}$/.test(compact) ? split(5, '-') : value.replace(/^(\d{5}) (\d{4})$/, '$1-$2')
    case 'CA':
    case 'IE':
      return compact.length === 6 || compact.length === 7 ? split(3) : value
    case 'GB':
      return compact.length >= 5 && compact.length <= 7 ? split(compact.length - 3) : value
    case 'NL':
      return /^\d{4}[A-Z]{2}$/.test(compact) ? split(4) : value
    case 'MT':
      return /^[A-Z]{3}\d{2,4}$/.test(compact) ? split(3) : value
    case 'CZ':
    case 'SK':
    case 'GR':
    case 'SE':
      return /^\d{5}$/.test(compact) ? split(3) : value
    case 'PL':
      return /^\d{5}$/.test(compact) ? split(2, '-') : value
    case 'PT':
      return /^\d{7}$/.test(compact) ? split(4, '-') : value
    case 'LV':
      return /^\d{4}$/.test(compact) ? `LV-${compact}` : /^LV\d{4}$/.test(compact) ? `LV-${compact.slice(2)}` : value
    default:
      return value
  }
}

const clean = (v: string | null | undefined) => {
  const t = (v ?? '').trim().replace(/\s+/g, ' ')
  return t === '' ? null : t
}

export interface AddressValidation {
  /** The address as it should be stored: trimmed, normalised, unused fields cleared. */
  value: PostalAddress
  errors: AddressErrors
}

/** Check an address against its country's rules and normalise it for storage. */
export function validatePostalAddress(input: PostalAddress): AddressValidation {
  const errors: AddressErrors = {}
  const countryCode = clean(input.countryCode)?.toUpperCase() ?? null
  const rules = rulesFor(countryCode)

  const value: PostalAddress = {
    addressType: ADDRESS_TYPES.includes(input.addressType as AddressType) ? input.addressType : 'Shipping',
    companyName: clean(input.companyName),
    attentionTo: clean(input.attentionTo),
    addressLine1: clean(input.addressLine1),
    addressLine2: clean(input.addressLine2),
    addressLine3: clean(input.addressLine3),
    city: clean(input.city),
    districtLocality: clean(input.districtLocality),
    stateProvinceRegion: null,
    stateProvinceCode: null,
    postalCode: null,
    countryCode,
  }

  if (!countryCode) errors.countryCode = 'Choose a country.'
  else if (!ISO_SET.has(countryCode)) errors.countryCode = `"${countryCode}" is not a country code.`

  if (!value.addressLine1) errors.addressLine1 = 'Street address is required.'
  if (!value.city) errors.city = `${rules.labels.city} is required.`

  if (rules.usesState) {
    const typed = clean(input.stateProvinceCode) ?? clean(input.stateProvinceRegion)
    if (rules.states) {
      const match = findState(rules, typed)
      if (match) {
        value.stateProvinceCode = match.code
        value.stateProvinceRegion = match.name
      } else if (typed) {
        errors.state = `Choose a ${rules.labels.state.toLowerCase()} from the list.`
      }
    } else {
      value.stateProvinceRegion = typed
    }
    if (!errors.state && rules.required.has('state') && !value.stateProvinceRegion) {
      errors.state = `${rules.labels.state} is required.`
    }
  } else {
    // Optional, kept for the record and never printed: the country's own format has no place for it.
    value.stateProvinceRegion = clean(input.stateProvinceRegion) ?? clean(input.stateProvinceCode)
  }

  if (rules.usesPostalCode) {
    const postal = normalizePostalCode(countryCode, input.postalCode)
    value.postalCode = postal || null
    if (!postal) {
      if (rules.required.has('postalCode')) errors.postalCode = `${rules.labels.postalCode} is required.`
    } else if (rules.postalPattern && !rules.postalPattern.test(postal)) {
      const examples = rules.postalExamples.slice(0, 2).join(' or ')
      // "postal code" mid-sentence; ZIP, PIN and Eircode keep their capitals.
      const name = rules.labels.postalCode.replace(/^([A-Z])(?=[a-z]+ )/, (c) => c.toLowerCase())
      errors.postalCode = `Enter a valid ${name}${examples ? `, e.g. ${examples}` : ''}.`
    }
  }

  return { value, errors }
}

/** Whether an address breaks its country's rules: what "Address needs review" means. */
export function addressNeedsReview(address: PostalAddress): boolean {
  return Object.keys(validatePostalAddress(address).errors).length > 0
}

/**
 * The address as the lines it prints in, in its country's layout:
 * `Tampa, FL 33602` in the US, `3500 Hasselt` in Belgium. Street lines share
 * one line, as the spec's examples do. The country's name is the last line
 * unless `includeCountry` is false.
 */
export function formatPostalAddress(
  address: PostalAddress,
  { includeCountry = true }: { includeCountry?: boolean } = {},
): string[] {
  const code = (address.countryCode ?? '').toUpperCase()
  const rules = rulesFor(code)
  let fmt = formatOf(code).f.fmt ?? FALLBACK_FMT
  const street = [address.addressLine1, address.addressLine2, address.addressLine3]
    .map((l) => l?.trim())
    .filter(Boolean)
    .join(', ')
  const state =
    code === 'ES'
      ? address.stateProvinceRegion
      : (address.stateProvinceCode ?? address.stateProvinceRegion)
  const values: Record<string, string> = {
    N: address.attentionTo?.trim() ?? '',
    O: address.companyName?.trim() ?? '',
    A: street,
    D: address.districtLocality?.trim() ?? '',
    C: address.city?.trim() ?? '',
    S: rules.usesState ? (state?.trim() ?? '') : '',
    Z: rules.usesPostalCode ? (address.postalCode?.trim() ?? '') : '',
    X: '',
  }
  // A country prefix on the postal code (CH-, FI-, HR-) goes with it.
  if (!values.Z) fmt = fmt.replace(/[A-Z]{1,2}-%Z/g, '%Z')
  // A district the country does not lay out goes under the street.
  if (values.D && !fmt.includes('%D')) fmt = fmt.replace('%A', '%A%n%D')

  const lines = fmt
    .split('%n')
    .map((line) =>
      line
        .replace(/%([A-Z])/g, (_, t: string) => values[t] ?? '')
        .replace(/\s+/g, ' ')
        .replace(/\s+,/g, ',')
        .replace(/^[\s,]+|[\s,]+$/g, '')
        .replace(/,\s*,/g, ','),
    )
    .filter(Boolean)
  if (includeCountry && code) lines.push(countryName(code))
  return lines
}

/** Common ways a country is typed that its English name does not cover. */
const COUNTRY_ALIASES: Record<string, string> = {
  usa: 'US', us: 'US', unitedstatesofamerica: 'US', america: 'US',
  uk: 'GB', greatbritain: 'GB', britain: 'GB', england: 'GB', scotland: 'GB', wales: 'GB', northernireland: 'GB',
  holland: 'NL', thenetherlands: 'NL', czechrepublic: 'CZ', deutschland: 'DE', espana: 'ES', osterreich: 'AT',
  schweiz: 'CH', suisse: 'CH', belgie: 'BE', belgique: 'BE', italia: 'IT', polska: 'PL', sverige: 'SE',
  norge: 'NO', danmark: 'DK', suomi: 'FI', eire: 'IE', hellas: 'GR', israel: 'IL',
}

/** A free-text country (an ISO code, an English name, a common alias) as its ISO code; null when unrecognised. */
export function countryCodeFromText(text: string | null | undefined): string | null {
  const t = (text ?? '').trim()
  if (!t) return null
  if (/^[A-Za-z]{2}$/.test(t) && ISO_SET.has(t.toUpperCase())) return t.toUpperCase()
  const want = fold(t)
  if (COUNTRY_ALIASES[want]) return COUNTRY_ALIASES[want]
  return ISO_COUNTRY_CODES.find((code) => fold(countryName(code)) === want) ?? null
}

/** A partner's address from before structured addresses: one street line and free-text country, state, city and ZIP. */
export interface LegacyAddress {
  address?: string | null
  city?: string | null
  state?: string | null
  zipCode?: string | null
  country?: string | null
}

/**
 * The legacy free-text address as a structured one: the country text mapped to
 * its code, a state name or code to the country's list entry. Unvalidated —
 * run `validatePostalAddress` or `addressNeedsReview` on it. Null when there
 * is no address at all.
 */
export function fromLegacyAddress(legacy: LegacyAddress): PostalAddress | null {
  const any = [legacy.address, legacy.city, legacy.state, legacy.zipCode, legacy.country].some((v) => v?.trim())
  if (!any) return null
  const countryCode = countryCodeFromText(legacy.country)
  const rules = rulesFor(countryCode)
  const state = findState(rules, legacy.state)
  return {
    addressType: 'Shipping',
    addressLine1: clean(legacy.address),
    city: clean(legacy.city),
    stateProvinceRegion: state?.name ?? clean(legacy.state),
    stateProvinceCode: state?.code ?? null,
    postalCode: clean(legacy.zipCode),
    countryCode,
  }
}

/** The flat legacy fields for a structured address, so readers of the old shape keep working. */
export function toLegacyAddress(address: PostalAddress): Required<LegacyAddress> {
  const street = [address.addressLine1, address.addressLine2, address.addressLine3]
    .map((l) => l?.trim())
    .filter(Boolean)
    .join(', ')
  return {
    address: street || null,
    city: address.city ?? null,
    state: address.stateProvinceCode ?? address.stateProvinceRegion ?? null,
    zipCode: address.postalCode ?? null,
    country: address.countryCode ? countryName(address.countryCode) : null,
  }
}
